import { Uuid } from "@/domains/global/identifiers";
import { IntervalObservable } from "@/store/common";
import { DataImportSourceMapObservable } from "@/store/data-imports/DataImportSourceMapObservable";
import { DataImportSyncModelData } from "@/store/data-imports/types";

import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";

import { WithAppStore } from "@/store/types";
import { DateTime, Duration } from "luxon";
import { computed, makeObservable, observable } from "mobx";
import type { JsonObject } from "type-fest";

/**
 * Because we coordinate imports using APIs + Sync Models, we are
 * using a "minimum" amount of time that we treat imports as being
 * in-progress before we show the "real" status.
 *
 * (The goal being that the import sync model should sync from the server to
 * the client before we show the "real" status.)
 *
 * We add some extra buffer our "standard" sync polling speed of 60 seconds.
 */
const MINIMUM_IMPORT_DURATION_SECONDS = 90;

export class DataImportObservable extends BaseSyncModel<DataImportSyncModelData> {
  public modelKind = SyncModelKind.DataImport;

  _isRunningInterval: IntervalObservable;

  constructor({
    id,
    data,
    store,
  }: {
    id: Uuid;
    data: SyncUpdateValue<DataImportSyncModelData>;
  } & WithAppStore) {
    super({ id, data, store });

    this._isRunningInterval = new IntervalObservable({
      duration: Duration.fromObject({ seconds: 5 }),
    });

    makeObservable(this, {
      _isRunningInterval: observable,
      modelKind: observable,
      ownedBySpaceAccountId: computed,
      kind: computed,
      startedAt: computed,
      endedAt: computed,
      endedWithError: computed,
      isRunning: computed,
      sourceMap: computed,
    });
  }

  get ownedBySpaceAccountId() {
    return this.modelData.owned_by_space_account_id;
  }

  get kind() {
    return this.modelData.kind;
  }

  get startedAt() {
    return this.modelData.started_at;
  }

  get endedAt() {
    return this.modelData.ended_at;
  }

  get endedWithError() {
    return this.modelData.ended_with_error;
  }

  get isRunning() {
    if (!this.startedAt) {
      return false;
    }
    /**
     * We check and see if it started within the minimum duration window.
     * (See the doc-comment on `MINIMUM_IMPORT_DURATION_SECONDS` for more details.)
     */
    const now = this._isRunningInterval.value;

    const secondsSinceStart = Math.abs(DateTime.fromISO(this.startedAt).diff(now).as("seconds"));
    if (secondsSinceStart < MINIMUM_IMPORT_DURATION_SECONDS) {
      return true;
    }

    /**
     * If it hasn't ended yet, then it's still running.
     */
    if (!this.endedAt) {
      return true;
    }

    return false;
  }

  get sourceMap(): DataImportSourceMapObservable | null {
    if (!this.modelData.source_map) {
      return null;
    }

    // Handles the fact that the values in source_map are `unknown` which is not compatible with `JsonObject`
    // TODO: define the source_map schema more strictly
    // Also see: https://mem-labs.slack.com/archives/C01GVGW4UCW/p1745515259696319
    const sourceMapJson = this.modelData.source_map as JsonObject;

    return DataImportSourceMapObservable.fromApiData({
      data: sourceMapJson,
    });
  }
}
